import { mainClient } from 'apollo/client';
import ToastStack from 'components/desktop/shared/toast-stack';
import { IconCheck, IconClose } from 'components/icons';
import { SITE_MAP } from 'globalConstants';
import {
  ACCEPT_FRIEND_REQUEST,
  AcceptFriendRequestData,
  AcceptFriendRequestVariables,
  DENY_FRIEND_REQUEST,
  DenyFriendRequestData,
  DenyFriendRequestVariables,
  FriendRequest,
} from 'graphql/main/mutations';
import { FRIEND_REQUEST_LIST } from 'graphql/main/queries/temp/friend-request-list';
import { useFlexMutation, useToast } from 'hooks';
import Image from 'next/image';
import Link from 'next/link';
import { Fragment } from 'react';

interface Props {
  friendRequest?: FriendRequest;
}

const FriendRequestCard = ({ friendRequest }: Props) => {
  const { toasts, addToast } = useToast();
  const { _id: friendId, friend } = friendRequest ?? {};
  const { id, fullName, type, avatar } = friend ?? {};

  const [acceptFriendRequest, { loading: loadingAcceptFriendRequest }] = useFlexMutation<
    AcceptFriendRequestData,
    AcceptFriendRequestVariables
  >('main', ACCEPT_FRIEND_REQUEST);
  const [denyFriendRequest, { loading: loadingDenyFriendRequest }] = useFlexMutation<
    DenyFriendRequestData,
    DenyFriendRequestVariables
  >('main', DENY_FRIEND_REQUEST);

  const accept = () => {
    if (friendId) {
      acceptFriendRequest({
        variables: {
          requestId: friendId,
        },
        onCompleted: (data) => {
          if (data.acceptFriendRequest?.success) {
            mainClient.refetchQueries({ include: [FRIEND_REQUEST_LIST] });
          }
        },
      });
    }
  };
  const deny = () => {
    if (friendId) {
      denyFriendRequest({
        variables: {
          requestId: friendId,
        },
        onCompleted: (data) => {
          if (data.denyFriendRequest?.success) {
            mainClient.refetchQueries({ include: [FRIEND_REQUEST_LIST] });
          }
        },
      });
    }
  };

  return (
    <Fragment>
      <div
        id='friend-request-card'
        className='w-full overflow-hidden rounded-[8px] bg-paper p-[12px]'
      >
        <div className='flex flex-col space-y-[14px]'>
          <div className='flex space-x-[12px]'>
            <div className='relative h-[48px] w-[48px] min-w-[48px] max-w-[48px] overflow-hidden rounded-full'>
              <Image
                src={avatar?.originalUrl ? avatar?.originalUrl : '/images/default-avatar.svg'}
                loading='eager'
                alt='user-image'
                fill
                sizes='auto'
                style={{
                  objectFit: 'cover',
                }}
              />
            </div>
            <div className='flex h-[48px] flex-col justify-around'>
              <Link
                href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${id}`}
                className='max-w-[280px] cursor-pointer truncate font-[600] text-text'
              >
                {fullName ?? ''}
              </Link>
              {Boolean(type) && (
                <div className='flex items-center space-x-[8px] text-[12px] leading-[15px] text-text2'>
                  <span>{type}</span>
                  {/* <BsDot /> */}
                  {/* {user.mutualFriends} <span>bạn chung</span> */}
                </div>
              )}
            </div>
          </div>
          <div className='flex justify-end space-x-[12px]'>
            <button
              type='button'
              disabled={loadingAcceptFriendRequest}
              className='text-paper flex w-[113px] h-[40px] items-center font-[600] justify-center space-x-[5px] rounded-[6px] bg-primary duration-[200ms] hover:bg-primary-light'
              onClick={accept}
            >
              <IconCheck className='min-h-[23px] min-w-[23px]' /> <span>Đồng ý</span>
            </button>
            <button
              disabled={loadingDenyFriendRequest}
              onClick={deny}
              className='flex w-[113px] text-text h-[40px] items-center font-[600] justify-center space-x-[5px] rounded-[6px] bg-paper border border-alert'
            >
              <IconClose className='min-h-[23px] min-w-[23px]' />
              <span>Từ chối</span>
            </button>
          </div>
        </div>
      </div>
      <ToastStack toasts={toasts} />
    </Fragment>
  );
};

export default FriendRequestCard;

export const FriendRequestCardSkeleton = () => (
  <div className='w-full overflow-hidden rounded-[8px] bg-paper p-[12px]'>
    <div className='flex flex-col space-y-[14px]'>
      <div className='flex space-x-[12px]'>
        <div className='relative h-[48px] w-[48px] min-w-[48px] max-w-[48px] overflow-hidden rounded-full bg-tertiary'></div>
        <div className='flex h-[48px] flex-col justify-around'>
          <div className='h-[14px] w-[180px] rounded bg-tertiary' />
          <div className='h-[14px] w-[100px] rounded bg-tertiary' />
        </div>
      </div>
    </div>
  </div>
);
